import { DOCUMENT_READER_FINISHED_RUN_STATES } from '@commenda/api/commenda-ai/document-reader/constants';
import {
  DocumentReaderRunStage,
  DocumentReaderRunState,
  type IDocumentReaderRunView,
} from '@commenda/api/commenda-ai/document-reader/types';
import { CurrencyHelper } from '@commenda/utils/currency';
import { Currency } from '@prisma/client';

export enum DocumentReadingStepStatus {
  DONE = 'DONE',
  CURRENT = 'CURRENT',
  UPCOMING = 'UPCOMING',
}

export enum DocumentReadingRunTone {
  WORKING = 'WORKING',
  WAITING = 'WAITING',
  DONE = 'DONE',
  PROBLEM = 'PROBLEM',
}

export interface IDocumentReadingStep {
  label: string;
  stages: readonly DocumentReaderRunStage[];
  /** Sends work to the AI, so it only runs once the price is approved. */
  isPaid: boolean;
}

export interface IDocumentReadingStepView {
  label: string;
  status: DocumentReadingStepStatus;
}

export interface IDocumentReadingRunCardView {
  headline: string;
  tone: DocumentReadingRunTone;
  steps: IDocumentReadingStepView[];
  percent: number;
  readingLine: string | null;
  modelCallLine: string | null;
  filesLine: string | null;
  spendLine: string | null;
  estimateLine: string | null;
  outcomeLine: string | null;
  isAwaitingApproval: boolean;
  isCompleted: boolean;
}

export class DocumentReadingRunHelper {
  /** The run's stages grouped into the six steps a person sees. */
  static readonly STEPS: readonly IDocumentReadingStep[] = [
    {
      label: 'Unzip',
      stages: [DocumentReaderRunStage.STORE, DocumentReaderRunStage.UNPACK],
      isPaid: false,
    },
    {
      label: 'Check files',
      stages: [DocumentReaderRunStage.CHECK_FILES],
      isPaid: false,
    },
    {
      label: 'Price',
      stages: [DocumentReaderRunStage.ESTIMATE],
      isPaid: false,
    },
    {
      label: 'Read pages',
      stages: [DocumentReaderRunStage.READ],
      isPaid: true,
    },
    {
      label: 'Understand',
      stages: [
        DocumentReaderRunStage.FIND,
        DocumentReaderRunStage.CARD,
        DocumentReaderRunStage.CUT,
        DocumentReaderRunStage.EXTRACT,
        DocumentReaderRunStage.CHECK_VALUES,
      ],
      isPaid: true,
    },
    {
      label: 'Sort by company',
      stages: [DocumentReaderRunStage.GROUP, DocumentReaderRunStage.MATCH],
      isPaid: true,
    },
  ];

  /** The free steps come first, so the bar's first share is theirs. */
  static readonly FREE_STEP_COUNT = DocumentReadingRunHelper.STEPS.filter(
    (step) => !step.isPaid
  ).length;

  /** The share of the bar the free steps fill; the AI calls fill the rest. */
  static readonly FREE_STEPS_PERCENT = 10;

  /** The stages that send AI calls, after the price is approved. */
  static readonly PAID_STAGES: readonly DocumentReaderRunStage[] =
    DocumentReadingRunHelper.STEPS.filter((step) => step.isPaid).flatMap(
      (step) => step.stages
    );

  static readonly STAGE_NAMES: Readonly<
    Record<DocumentReaderRunStage, string>
  > = {
    [DocumentReaderRunStage.STORE]: 'Checking the upload',
    [DocumentReaderRunStage.UNPACK]: 'Unzipping the data room',
    [DocumentReaderRunStage.CHECK_FILES]: 'Checking every file',
    [DocumentReaderRunStage.ESTIMATE]: 'Working out the price',
    [DocumentReaderRunStage.READ]: 'Reading pages',
    [DocumentReaderRunStage.FIND]: 'Finding documents',
    [DocumentReaderRunStage.CARD]: 'Labelling documents',
    [DocumentReaderRunStage.CUT]: 'Cutting documents apart',
    [DocumentReaderRunStage.EXTRACT]: 'Pulling out facts',
    [DocumentReaderRunStage.CHECK_VALUES]: 'Checking facts',
    [DocumentReaderRunStage.GROUP]: 'Sorting by company',
    [DocumentReaderRunStage.MATCH]: 'Matching with Commenda',
    [DocumentReaderRunStage.DONE]: 'Done',
  };

  static isFinished(state: DocumentReaderRunState): boolean {
    return DOCUMENT_READER_FINISHED_RUN_STATES.includes(state);
  }

  static getCardView(
    run: Readonly<IDocumentReaderRunView>
  ): IDocumentReadingRunCardView {
    const steps = DocumentReadingRunHelper.getSteps(run);
    const isAwaitingApproval =
      run.state === DocumentReaderRunState.AWAITING_APPROVAL;

    return {
      headline: DocumentReadingRunHelper.getHeadline(run),
      tone: DocumentReadingRunHelper.getTone(run.state),
      steps,
      percent: DocumentReadingRunHelper.getPercent(run, steps),
      readingLine: DocumentReadingRunHelper.getReadingLine(run),
      modelCallLine: DocumentReadingRunHelper.getModelCallLine(run),
      filesLine: DocumentReadingRunHelper.getFilesLine(run),
      spendLine: DocumentReadingRunHelper.getSpendLine(run),
      estimateLine: isAwaitingApproval
        ? DocumentReadingRunHelper.getEstimateLine(run)
        : null,
      outcomeLine: DocumentReadingRunHelper.getOutcomeLine(run),
      isAwaitingApproval,
      isCompleted: run.state === DocumentReaderRunState.COMPLETED,
    };
  }

  /** Null when the backend's amount does not parse, so it never reads as $0.00. */
  static formatUsd(amount: string): string | null {
    const value = Number(amount);
    return Number.isFinite(value)
      ? CurrencyHelper.formatCurrency(Currency.USD, value)
      : null;
  }

  private static getSteps(
    run: Readonly<IDocumentReaderRunView>
  ): IDocumentReadingStepView[] {
    const isAllDone =
      run.state === DocumentReaderRunState.COMPLETED ||
      run.stage === DocumentReaderRunStage.DONE;
    const currentIndex = DocumentReadingRunHelper.STEPS.findIndex((step) =>
      step.stages.includes(run.stage)
    );

    return DocumentReadingRunHelper.STEPS.map((step, index) => ({
      label: step.label,
      status:
        isAllDone || index < currentIndex
          ? DocumentReadingStepStatus.DONE
          : index === currentIndex
            ? DocumentReadingStepStatus.CURRENT
            : DocumentReadingStepStatus.UPCOMING,
    }));
  }

  private static getHeadline(run: Readonly<IDocumentReaderRunView>): string {
    if (run.state === DocumentReaderRunState.QUEUED) return 'Waiting to start';
    if (run.state === DocumentReaderRunState.AWAITING_APPROVAL)
      return 'Price ready, waiting for your approval';
    if (run.state === DocumentReaderRunState.COMPLETED)
      return 'Finished reading the data room';
    if (run.state === DocumentReaderRunState.STOPPED) return 'Reading stopped';
    if (run.state === DocumentReaderRunState.FAILED) return 'Reading failed';
    return DocumentReadingRunHelper.STAGE_NAMES[run.stage];
  }

  private static getTone(
    state: DocumentReaderRunState
  ): DocumentReadingRunTone {
    if (state === DocumentReaderRunState.AWAITING_APPROVAL)
      return DocumentReadingRunTone.WAITING;
    if (state === DocumentReaderRunState.COMPLETED)
      return DocumentReadingRunTone.DONE;
    if (
      state === DocumentReaderRunState.STOPPED ||
      state === DocumentReaderRunState.FAILED
    )
      return DocumentReadingRunTone.PROBLEM;
    return DocumentReadingRunTone.WORKING;
  }

  /** One scale that only moves forward: the free steps before the price fill the first 10%, then the
   * share of the estimated AI calls saved fills the rest. A stopped or failed run keeps its place, and
   * nothing shows 100% until the run has finished. */
  private static getPercent(
    run: Readonly<IDocumentReaderRunView>,
    steps: readonly IDocumentReadingStepView[]
  ): number {
    if (run.state === DocumentReaderRunState.COMPLETED) return 100;
    const estimatedCalls = run.estimate?.modelCallCount ?? 0;
    if (
      DocumentReadingRunHelper.PAID_STAGES.includes(run.stage) &&
      estimatedCalls > 0
    ) {
      const paidShare = Math.min(1, run.completedModelCalls / estimatedCalls);
      return Math.min(
        99,
        DocumentReadingRunHelper.FREE_STEPS_PERCENT +
          Math.round(
            paidShare * (100 - DocumentReadingRunHelper.FREE_STEPS_PERCENT)
          )
      );
    }
    const freeStepsDone = steps
      .slice(0, DocumentReadingRunHelper.FREE_STEP_COUNT)
      .filter((step) => step.status === DocumentReadingStepStatus.DONE).length;
    return Math.round(
      (freeStepsDone / DocumentReadingRunHelper.FREE_STEP_COUNT) *
        DocumentReadingRunHelper.FREE_STEPS_PERCENT
    );
  }

  /** Live on every refresh: saved AI calls come from the run's own records, while the stage counters
   * only move when a stage finishes. */
  private static getModelCallLine(
    run: Readonly<IDocumentReaderRunView>
  ): string | null {
    if (!run.estimate || !DocumentReadingRunHelper.isPaidWorkUnderway(run))
      return null;
    return `${DocumentReadingRunHelper.formatCount(run.completedModelCalls)} of about ${DocumentReadingRunHelper.formatCount(run.estimate.modelCallCount)} AI calls done`;
  }

  private static isPaidWorkUnderway(
    run: Readonly<IDocumentReaderRunView>
  ): boolean {
    return (
      run.state === DocumentReaderRunState.RUNNING &&
      DocumentReadingRunHelper.PAID_STAGES.includes(run.stage)
    );
  }

  private static getReadingLine(
    run: Readonly<IDocumentReaderRunView>
  ): string | null {
    if (run.stage !== DocumentReaderRunStage.READ) return null;
    const pagesToRead = run.estimate?.pagesToRead ?? run.counters.pages;
    const pagesLine = `${DocumentReadingRunHelper.formatCount(run.counters.pagesRead)} of ${DocumentReadingRunHelper.formatCount(pagesToRead)} pages read`;
    if (run.readingSecondsLeft === null) return pagesLine;
    const minutesLeft = Math.round(run.readingSecondsLeft / 60);
    return minutesLeft < 1
      ? `${pagesLine}, under a minute left`
      : `${pagesLine}, about ${minutesLeft} min left`;
  }

  private static getFilesLine(
    run: Readonly<IDocumentReaderRunView>
  ): string | null {
    const { filesReceived, filesRead, filesCopied, filesNotSupported } =
      run.counters;
    if (filesReceived === 0) return null;
    const parts = [
      `${DocumentReadingRunHelper.formatCount(filesRead)} files to read`,
      `${DocumentReadingRunHelper.formatCount(filesCopied)} copies skipped`,
    ];
    if (filesNotSupported > 0)
      parts.push(
        `${DocumentReadingRunHelper.formatCount(filesNotSupported)} not supported`
      );
    if (run.counters.filesFailed > 0)
      parts.push(
        `${DocumentReadingRunHelper.formatCount(run.counters.filesFailed)} failed`
      );
    return parts.join(' · ');
  }

  private static getSpendLine(
    run: Readonly<IDocumentReaderRunView>
  ): string | null {
    const spent = DocumentReadingRunHelper.formatUsd(run.costUsd);
    if (!spent) return null;
    const estimated = run.estimate
      ? DocumentReadingRunHelper.formatUsd(run.estimate.costUsd)
      : null;
    if (estimated) return `${spent} spent of ${estimated} estimated`;
    return Number(run.costUsd) > 0 ? `${spent} spent` : null;
  }

  private static getEstimateLine(
    run: Readonly<IDocumentReaderRunView>
  ): string | null {
    if (!run.estimate) return null;
    const estimated = DocumentReadingRunHelper.formatUsd(run.estimate.costUsd);
    const pages = DocumentReadingRunHelper.formatCount(
      run.estimate.pagesToRead
    );
    return estimated
      ? `Estimated ${estimated} for ${pages} pages. Nothing is spent until you approve.`
      : `The price for ${pages} pages could not be shown. Ask the chat for it before you approve.`;
  }

  private static getOutcomeLine(
    run: Readonly<IDocumentReaderRunView>
  ): string | null {
    const { documents, companies, valuesChecked, valuesFlagged } = run.counters;
    if (run.state === DocumentReaderRunState.COMPLETED)
      return `${DocumentReadingRunHelper.formatCount(documents)} documents, ${DocumentReadingRunHelper.formatCount(companies)} companies, ${DocumentReadingRunHelper.formatCount(valuesChecked)} facts checked, ${DocumentReadingRunHelper.formatCount(valuesFlagged)} to review`;
    if (run.state === DocumentReaderRunState.STOPPED) return run.stopReason;
    if (run.state === DocumentReaderRunState.FAILED) return run.failureMessage;
    return null;
  }

  private static formatCount(value: number): string {
    return value.toLocaleString('en-US');
  }
}
